import React, { useEffect, useState } from 'react';
import { BottomSheet } from '../../../shared/ui';
import { sitesV2Api } from '../../../api/sitesV2Api';
import { siteExecutorsApi, SiteExecutor } from '../../../api/siteExecutorsApi';
import './CreateInviteModal.css';

interface CreateInviteModalProps {
  isOpen: boolean;
  siteId: number;
  onClose: () => void;
}

type InviteType = 'new' | 'executor';

const CreateInviteModal: React.FC<CreateInviteModalProps> = ({
  isOpen,
  siteId,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [createdForName, setCreatedForName] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const [inviteType, setInviteType] = useState<InviteType>('new');
  const [externalExecutors, setExternalExecutors] = useState<SiteExecutor[]>([]);
  const [executorsLoading, setExecutorsLoading] = useState(false);
  const [selectedExecutorId, setSelectedExecutorId] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setExecutorsLoading(true);
    siteExecutorsApi
      .getSiteExecutors(siteId)
      .then((executors) => {
        if (cancelled) return;
        setExternalExecutors(executors.filter((e) => e.isExternal));
      })
      .catch((err) => {
        console.error('Ошибка загрузки исполнителей:', err);
        if (!cancelled) setExternalExecutors([]);
      })
      .finally(() => {
        if (!cancelled) setExecutorsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, siteId]);

  const handleCreateInvite = async () => {
    try {
      setLoading(true);
      setError(null);
      setCopied(false);

      const targetExecutorId = inviteType === 'executor' ? selectedExecutorId ?? undefined : undefined;
      const invitation = await sitesV2Api.createInvitation(siteId, targetExecutorId);

      // Формируем полный URL
      const fullUrl = `${window.location.origin}${invitation.inviteUrl}`;
      setInviteUrl(fullUrl);
      setExpiresAt(invitation.expiresAt);
      setCreatedForName(invitation.targetExecutorName ?? null);
    } catch (err: any) {
      console.error('Ошибка создания приглашения:', err);
      setError(err.response?.data?.message || err.response?.data || err.message || 'Ошибка создания приглашения');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!inviteUrl) return;

    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Ошибка копирования:', err);
      setError('Не удалось скопировать ссылку');
    }
  };

  const handleClose = () => {
    setInviteUrl(null);
    setExpiresAt(null);
    setCreatedForName(null);
    setError(null);
    setCopied(false);
    setInviteType('new');
    setSelectedExecutorId(null);
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  const hasExternalExecutors = externalExecutors.length > 0;
  const createDisabled = loading || (inviteType === 'executor' && selectedExecutorId === null);

  return (
    <BottomSheet
      isOpen={isOpen}
      title={inviteUrl ? "Приглашение создано" : "Создать приглашение"}
      onClose={handleClose}
      size="auto"
    >
      <div className="bottom-sheet-form">
        {error && (
          <div className="error-message">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        {inviteUrl ? (
          <>
            {/* Показываем созданную ссылку */}
            <div className="form-group">
              {createdForName && (
                <div className="invite-target-badge">
                  <i className="ri-user-received-line"></i>
                  <span>Приглашение на место «{createdForName}»</span>
                </div>
              )}
              <label>Ссылка для приглашения</label>
              <div className="invite-url-container">
                <input
                  type="text"
                  value={inviteUrl}
                  readOnly
                  className="invite-url-input"
                />
                <button
                  className="copy-button"
                  onClick={handleCopyLink}
                  title={copied ? "Скопировано!" : "Копировать"}
                >
                  {copied ? (
                    <i className="ri-check-line"></i>
                  ) : (
                    <i className="ri-file-copy-line"></i>
                  )}
                </button>
              </div>
              {expiresAt && (
                <div className="invite-expires">
                  <i className="ri-time-line"></i>
                  <span>Действительно до: {new Date(expiresAt).toLocaleString('ru-RU')}</span>
                </div>
              )}
              <div className="invite-note">
                <i className="ri-information-line"></i>
                <span>Ссылка действительна 24 часа. Отправьте её тому, кого хотите пригласить.</span>
              </div>
            </div>

            {/* Кнопки действий */}
            <div className="form-actions">
              <button
                className="action-button primary"
                onClick={handleClose}
              >
                Готово
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Выбор типа приглашения */}
            <div className="invite-type-options">
              <button
                type="button"
                className={`invite-type-option ${inviteType === 'new' ? 'selected' : ''}`}
                onClick={() => setInviteType('new')}
              >
                <i className="ri-user-add-line"></i>
                <div className="invite-type-texts">
                  <span className="invite-type-title">Новый участник</span>
                  <span className="invite-type-desc">Начнёт с чистого листа — без смен и начислений</span>
                </div>
              </button>

              <button
                type="button"
                className={`invite-type-option ${inviteType === 'executor' ? 'selected' : ''}`}
                onClick={() => hasExternalExecutors && setInviteType('executor')}
                disabled={!hasExternalExecutors}
              >
                <i className="ri-user-received-line"></i>
                <div className="invite-type-texts">
                  <span className="invite-type-title">На место исполнителя</span>
                  <span className="invite-type-desc">
                    {hasExternalExecutors
                      ? 'Получит всё, что записано на выбранного исполнителя: смены, начисления, ставку'
                      : 'На объекте нет внешних исполнителей'}
                  </span>
                </div>
              </button>
            </div>

            {/* Выбор внешнего исполнителя */}
            {inviteType === 'executor' && (
              <div className="form-group">
                <label>Чьё место займёт приглашённый</label>
                {executorsLoading ? (
                  <div className="loading-state">
                    <i className="ri-loader-4-line ri-spin"></i>
                    <span>Загрузка исполнителей...</span>
                  </div>
                ) : (
                  <div className="invite-executor-list">
                    {externalExecutors.map((executor) => (
                      <button
                        type="button"
                        key={executor.id}
                        className={`invite-executor-item ${selectedExecutorId === executor.id ? 'selected' : ''}`}
                        onClick={() => setSelectedExecutorId(executor.id)}
                      >
                        <i className={selectedExecutorId === executor.id ? 'ri-radio-button-line' : 'ri-checkbox-blank-circle-line'}></i>
                        <span>{executor.displayName}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Кнопки действий */}
            <div className="form-actions">
              <button
                className="action-button secondary"
                onClick={handleClose}
              >
                Отмена
              </button>
              <button
                className="action-button primary"
                onClick={handleCreateInvite}
                disabled={createDisabled}
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line ri-spin"></i>
                    Создание...
                  </>
                ) : (
                  <>
                    <i className="ri-link"></i>
                    Создать приглашение
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </BottomSheet>
  );
};

export default CreateInviteModal;
